import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import apiService from '../../../services/api';
import styles from '../../../styles/SystemSettings.module.css';

interface LoginHistory {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  timestamp: string;
  status: 'success' | 'failed';
}

const SystemSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Ensure we only load security settings once (even in React StrictMode)
  const hasLoadedSecuritySettingsRef = useRef(false);

  // Load security settings (MFA + login history) from backend
  useEffect(() => {
    if (hasLoadedSecuritySettingsRef.current) {
      return;
    }
    hasLoadedSecuritySettingsRef.current = true;

    const loadSecuritySettings = async () => {
      try {
        showLoader('Loading security settings...');
        const response = await apiService.getSecuritySettings();

        if (response.status === 'success') {
          const data: any = response.data || {};

          // MFA flag can come as mfa_enabled or mfaEnabled
          const mfa =
            typeof data.mfa_enabled !== 'undefined'
              ? data.mfa_enabled
              : typeof data.mfaEnabled !== 'undefined'
                ? data.mfaEnabled
                : false;
          setMfaEnabled(!!mfa);

          // Login history can come as login_history or loginHistory
          const history = (data.login_history || data.loginHistory || []) as any[];
          if (Array.isArray(history)) {
            const mappedHistory: LoginHistory[] = history.map((item, index) => ({
              id: String(item.id ?? index),
              device: item.device ?? item.user_agent ?? 'Unknown device',
              browser: item.browser ?? 'Unknown browser',
              location: item.location ?? 'Unknown location',
              ip: item.ip ?? item.ip_address ?? 'Unknown IP',
              timestamp: item.timestamp ?? item.created_at ?? new Date().toISOString(),
              status: item.status === 'failed' ? 'failed' : 'success',
            }));
            setLoginHistory(mappedHistory);
          }
        } else {
          notificationService.error(response.message || 'Failed to load security settings');
        }
      } catch (error: any) {
        console.error('Failed to load security settings', error);
        notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load security settings');
      } finally {
        hideLoader();
      }
    };

    loadSecuritySettings();
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isChangingPassword) {
      return;
    }

    if (passwords.new !== passwords.confirm) {
      notificationService.error('New passwords do not match');
      return;
    }

    if (passwords.new.length < 8) {
      notificationService.error('Password must be at least 8 characters');
      return;
    }

    try {
      setIsChangingPassword(true);
      showLoader('Changing password...');
      const response = await apiService.changePassword({
        current_password: passwords.current,
        new_password: passwords.new,
        confirm_password: passwords.confirm,
      });
      if (response.status === 'success') {
        notificationService.success(response.message || 'Password changed successfully');
        setPasswords({ current: '', new: '', confirm: '' });

        const redirectUrl =
          (response as any).redirect_url ||
          `${apiService.getBaseURL()}/auth/signout/`;
        window.location.href = redirectUrl;
      } else {
        notificationService.error(response.message || 'Failed to change password');
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message;
      notificationService.error(backendMessage || 'Failed to change password');
    } finally {
      hideLoader();
      setIsChangingPassword(false);
    }
  };

  const handleToggleMFA = async () => {
    try {
      if (mfaEnabled) {
        // Disabling MFA
        showLoader('Disabling 2FA...');
        const response = await apiService.updateSecuritySettings({
          mfa_enabled: false,
          mfaEnabled: false,
        });
        if (response.status === 'success') {
          setMfaEnabled(false);
          notificationService.success('2FA disabled');
        } else {
          notificationService.error(response.message || 'Failed to disable 2FA');
        }
      } else {
        // Enabling MFA - navigate to setup page
        hideLoader();
        navigate('/mfa/setup');
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message;
      notificationService.error(backendMessage || 'Failed to update 2FA');
    } finally {
      hideLoader();
    }
  };

  const getUserName = () => user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'User';
  const getUserInitial = () => user ? (user.first_name?.[0] || 'U').toUpperCase() : 'U';
  const getDesignation = () => (user as any)?.designation || 'Member';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <h1>Settings</h1>
        <p>Manage your account and security preferences</p>
      </div>

      <div className={styles.settingsLayout}>
        {/* Profile Card - Left Sidebar */}
        <div className={styles.profileCard}>
          <div className={styles.profileBanner}></div>
          <div className={styles.profileContent}>
            <div className={styles.profileAvatar}>{getUserInitial()}</div>
            <h3 className={styles.profileName}>{getUserName()}</h3>
            <p className={styles.profileEmail}>{user?.email}</p>
            <div className={styles.profileStats}>
              <div className={styles.profileStat}>
                <div className={`${styles.profileStatValue} ${mfaEnabled ? styles.enabled : styles.notEnabled}`}>
                  {mfaEnabled ? 'Enabled' : 'Not Enabled'}
                </div>
                <div className={styles.profileStatLabel}>2FA Status</div>
              </div>
              <div className={styles.profileStat}>
                <div className={styles.profileStatValue} style={{ textTransform: 'capitalize' }}>{getDesignation()}</div>
                <div className={styles.profileStatLabel}>Role</div>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Cards */}
        <div className={styles.settingsCards}>
          {/* Change Password */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3>Change Password</h3>
            </div>
            <form onSubmit={handleChangePassword} className={styles.cardBody}>
              <div className={styles.formGroup}>
                <label>Current Password</label>
                <div className={styles.inputWrapper}>
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwords.current}
                    onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                  >
                    {showPasswords.current ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>New Password</label>
                <div className={styles.inputWrapper}>
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwords.new}
                    onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                  >
                    {showPasswords.new ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Confirm New Password</label>
                <div className={styles.inputWrapper}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwords.confirm}
                    onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                  >
                    {showPasswords.confirm ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>

          {/* Two-Factor Authentication */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={`${styles.cardIcon} ${styles.green}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Two-Factor Authentication</h3>
            </div>
            <div className={styles.cardBody}>
              <p className={styles.mfaDesc}>
                Add an extra layer of security to your account. When enabled, you'll need to enter a verification code from your authenticator app in addition to your password.
              </p>
              <div className={styles.mfaToggle}>
                <div className={styles.mfaStatus}>
                  <span className={`${styles.statusDot} ${mfaEnabled ? styles.active : ''}`}></span>
                  <span>{mfaEnabled ? '2FA is Enabled' : '2FA is Disabled'}</span>
                </div>
                <button
                  className={`${styles.mfaBtn} ${mfaEnabled ? styles.danger : styles.primary}`}
                  onClick={handleToggleMFA}
                >
                  {mfaEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                </button>
              </div>
            </div>
          </div>

          {/* Login History */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={`${styles.cardIcon} ${styles.orange}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3>Recent Login History</h3>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.loginHistory}>
                {loginHistory.map((login) => (
                  <div key={login.id} className={`${styles.loginItem} ${login.status === 'failed' ? styles.failedItem : ''}`}>
                    <div className={styles.loginMain}>
                      <div className={`${styles.loginStatus} ${login.status === 'success' ? styles.success : styles.failed}`}>
                        {login.status === 'success' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                        )}
                      </div>
                      <div className={styles.loginInfo}>
                        <div className={styles.loginDevice}>{login.device}</div>
                        <div className={styles.loginBrowser}>{login.browser}</div>
                      </div>
                    </div>
                    <div className={styles.loginDetails}>
                      <div className={styles.loginDetailRow}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                        <span>{login.location}</span>
                      </div>
                      <div className={styles.loginDetailRow}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2" ry="2" /><rect x="2" y="14" width="20" height="8" rx="2" ry="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></svg>
                        <span>{login.ip}</span>
                      </div>
                    </div>
                    <div className={styles.loginRight}>
                      <div className={styles.loginTime}>{formatDate(login.timestamp)}</div>
                      <div className={`${styles.loginBadge} ${login.status === 'success' ? styles.successBadge : styles.failedBadge}`}>
                        {login.status === 'success' ? 'Success' : 'Failed'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
